import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, Inbox } from "lucide-react";
import { toast } from "sonner";
import { useAuthContext } from "@/contexts/AuthContext";
import { GC_VLAD_ORG_ID } from "@/lib/gcSubtrades";
import { Navigate } from "react-router-dom";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function ReceiptsInbox() {
  const { orgId } = useAuthContext();
  const qc = useQueryClient();
  const [polling, setPolling] = useState(false);

  if (orgId && orgId !== GC_VLAD_ORG_ID) {
    return <Navigate to="/" replace />;
  }

  const { data: emails = [], isLoading } = useQuery({
    queryKey: ["supplier_emails_inbox"],
    queryFn: async () => {
      const { data } = await supabase
        .from("supplier_emails" as any)
        .select("*")
        .order("received_at", { ascending: false })
        .limit(100);
      return (data as any[]) ?? [];
    },
  });

  const { data: jobs = [] } = useQuery({
    queryKey: ["jobs_for_assign"],
    queryFn: async () => {
      const { data } = await supabase
        .from("jobs")
        .select("id, name, address")
        .order("created_at", { ascending: false })
        .limit(200);
      return (data as any[]) ?? [];
    },
  });

  const pollNow = async () => {
    setPolling(true);
    try {
      const { data, error } = await supabase.functions.invoke("receipts-gmail-poll");
      if (error) throw error;
      toast.success(`Scanned ${data?.scanned ?? 0}, imported ${data?.inserted ?? 0}`);
      qc.invalidateQueries({ queryKey: ["supplier_emails_inbox"] });
    } catch (e: any) {
      toast.error(e?.message ?? "Poll failed");
    } finally {
      setPolling(false);
    }
  };

  const assign = async (id: string, jobId: string) => {
    const { error } = await supabase
      .from("supplier_emails" as any)
      .update({ job_id: jobId, status: "assigned" })
      .eq("id", id);
    if (error) return toast.error(error.message);
    await supabase
      .from("supplier_line_items" as any)
      .update({ job_id: jobId })
      .eq("supplier_email_id", id);
    toast.success("Assigned to job");
    qc.invalidateQueries({ queryKey: ["supplier_emails_inbox"] });
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Inbox className="h-6 w-6" /> Receipts Inbox
        </h1>
        <Button onClick={pollNow} disabled={polling} variant="outline">
          <RefreshCw className={`h-4 w-4 mr-2 ${polling ? "animate-spin" : ""}`} />
          Sync Gmail
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : emails.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            No supplier emails yet. Hit <span className="font-semibold">Sync Gmail</span> to pull recent receipts.
          </CardContent>
        </Card>
      ) : (
        emails.map((e: any) => (
          <Card key={e.id}>
            <CardContent className="py-3 flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">
                  {e.subject ?? "(no subject)"}
                </div>
                <div className="text-xs text-muted-foreground truncate">
                  {e.from_email} · {e.vendor ?? "—"}{" "}
                  {e.invoice_number ? `· #${e.invoice_number}` : ""}
                </div>
              </div>
              <Badge
                variant={
                  e.status === "assigned"
                    ? "default"
                    : e.status === "needs_review"
                    ? "destructive"
                    : "secondary"
                }
              >
                {e.status}
              </Badge>
              <div className="w-64">
                <Select
                  value={e.job_id ?? ""}
                  onValueChange={(v) => assign(e.id, v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Assign to job…" />
                  </SelectTrigger>
                  <SelectContent>
                    {jobs.map((j: any) => (
                      <SelectItem key={j.id} value={j.id}>
                        {j.name ?? j.address ?? j.id.slice(0, 8)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}
